import { useState, useEffect } from 'react'
import { useAuthStore } from '../store/authStore'
import { supabaseClient } from '../config/supabase'
import type { AdminScope } from '../types'

interface AdminScopeData {
  adminScope: AdminScope | null
  allowedDepartments: string[] | null
  department: string | null
  isSuperAdmin: boolean
  loading: boolean
  canManageDepartment: (deptName: string) => boolean
}

export function useAdminScope(): AdminScopeData {
  const { user } = useAuthStore()
  const [adminScope, setAdminScope] = useState<AdminScope | null>(null)
  const [allowedDepartments, setAllowedDepartments] = useState<string[] | null>(null)
  const [department, setDepartment] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchScope = async () => {
      if (!user || user.role !== 'admin') {
        setLoading(false)
        return
      }

      try {
        const { data, error } = await supabaseClient
          .from('admin_profiles')
          .select('admin_scope, allowed_departments, department')
          .eq('user_id', user.id)
          .single()

        if (error) throw error

        if (data) {
          const scope = data.admin_scope || 'department'
          const dept = data.department || null
          const allowed = Array.isArray(data.allowed_departments)
            ? data.allowed_departments.filter(Boolean)
            : null

          setAdminScope(scope)
          setDepartment(dept)

          // Defensive fallback: for department admins with broken/null scope data,
          // use their own department as the allowed scope.
          if (scope !== 'super_admin' && (!allowed || allowed.length === 0) && dept) {
            setAllowedDepartments([dept])
          } else {
            setAllowedDepartments(allowed)
          }
        }
      } catch (err) {
        console.error('Error fetching admin scope:', err)
      } finally {
        setLoading(false)
      }
    }

    fetchScope()
  }, [user])

  const isSuperAdmin = adminScope === 'super_admin'

  const canManageDepartment = (deptName: string): boolean => {
    if (isSuperAdmin) return true
    if (!allowedDepartments) return false
    return allowedDepartments.includes(deptName)
  }

  return {
    adminScope,
    allowedDepartments,
    department,
    isSuperAdmin,
    loading,
    canManageDepartment,
  }
}
